import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { NotificationBell } from "../../../components/ui/NotificationBell";
import { NotificationPanel, NotificationFilter } from "../../../components/ui/NotificationPanel";
import { NotificationItem } from "../../../components/ui/NotificationItem";
import { useAdminNotifications, useUnreadNotificationCount } from "../../../hooks/admin/useNotifications";
import { AdminNotification, NOTIFICATION_TYPE_MAP } from "../../../types/notification";
import { getUserErrorMessage } from "../../../api/apiErrors";
import { showError } from "../../../utils/alert";
import { formatRelativeTime } from "../../../utils/formatRelativeTime";
import {
    getNotificationRoute,
    shouldClosePanelAfterNotification,
} from "../../../utils/notificationHandlers";
import {
    markNotificationAsRead,
    markAllNotificationsAsRead,
} from "../../../utils/notificationService";
import api from "../../../api/apiClient";

const NOTIFICATION_FILTERS: NotificationFilter[] = [
    { key: "", label: "All" },
    { key: "offer_created", label: "Offers" },
    { key: "store_suggested", label: "Suggestions" },
    { key: "owner_registered", label: "Owners" },
    { key: "merchant_registered", label: "Merchants" },
];

const PAGE_LIMIT = 10;

export const AdminNotificationBell: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const [currentPage, setCurrentPage] = useState(1);
    const [selectedFilter, setSelectedFilter] = useState("");
    const [allNotifications, setAllNotifications] = useState<AdminNotification[]>([]);
    const [markingIds, setMarkingIds] = useState<number[]>([]);
    const [isLoadingMore, setIsLoadingMore] = useState(false);

    const { data: notificationsData, isLoading } = useAdminNotifications({
        page: currentPage,
        limit: PAGE_LIMIT,
        key: selectedFilter,
    });

    const { data: unreadCount = 0 } = useUnreadNotificationCount();

    useEffect(() => {
        if (!notificationsData?.data) return;

        if (currentPage === 1) {
            setAllNotifications(notificationsData.data);
        } else {
            setAllNotifications((prev) => [...prev, ...notificationsData.data]);
        }
    }, [notificationsData?.data, currentPage]);

    const handleMarkAsRead = useCallback(
        async (notificationId: number) => {
            const notification = allNotifications.find((n) => n.id === notificationId);
            if (!notification || notification.isRead) return;

            setMarkingIds((prev) => [...prev, notificationId]);
            setAllNotifications((prev) =>
                prev.map((n) =>
                    n.id === notificationId ? { ...n, isRead: true } : n
                )
            );

            try {
                await markNotificationAsRead(notificationId);
                queryClient.invalidateQueries({ queryKey: ["admin-notifications-unread-count"] });
            } catch (error) {
                setAllNotifications((prev) =>
                    prev.map((n) =>
                        n.id === notificationId ? { ...n, isRead: false } : n
                    )
                );
                showError(getUserErrorMessage(error));
            } finally {
                setMarkingIds((prev) => prev.filter((id) => id !== notificationId));
            }
        },
        [allNotifications, queryClient]
    );

    const handleMarkAllAsRead = useCallback(async () => {
        if (unreadCount === 0) return;

        const previousNotifications = allNotifications;
        setAllNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));

        try {
            await markAllNotificationsAsRead();
            queryClient.invalidateQueries({ queryKey: ["admin-notifications-unread-count"] });
            queryClient.invalidateQueries({ queryKey: ["admin-notifications"] });
        } catch (error) {
            setAllNotifications(previousNotifications);
            queryClient.invalidateQueries({ queryKey: ["admin-notifications"] });
            showError(getUserErrorMessage(error));
        }
    }, [unreadCount, queryClient, allNotifications]);

    const handleLoadMore = useCallback(async () => {
        if (isLoadingMore || currentPage >= (notificationsData?.totalPages || 1)) return;

        setIsLoadingMore(true);
        try {
            const nextPage = currentPage + 1;
            const queryParams = new URLSearchParams({
                page: nextPage.toString(),
                limit: PAGE_LIMIT.toString(),
            });
            if (selectedFilter) queryParams.append("key", selectedFilter);

            const nextData = await queryClient.fetchQuery({
                queryKey: ["admin-notifications", nextPage, PAGE_LIMIT, selectedFilter],
                queryFn: () => api.get(`admin-notification?${queryParams}`).then((res) => res.data),
            });

            setAllNotifications((prev) => [...prev, ...nextData.data]);
            setCurrentPage(nextPage);
        } catch (error) {
            showError(getUserErrorMessage(error));
        } finally {
            setIsLoadingMore(false);
        }
    }, [currentPage, isLoadingMore, queryClient, selectedFilter, notificationsData?.totalPages]);

    const handleFilterChange = useCallback((filterKey: string) => {
        setSelectedFilter(filterKey);
        setCurrentPage(1);
        setAllNotifications([]);
    }, []);

    const handleNotificationClick = useCallback(
        (notification: AdminNotification, closePanel: () => void) => {
            if (!notification.isRead) {
                handleMarkAsRead(notification.id);
            }

            const route = getNotificationRoute(notification.key, notification.metadata);
            const shouldClose = shouldClosePanelAfterNotification(notification.key);

            if (shouldClose) {
                closePanel();
            }

            navigate(route);
        },
        [handleMarkAsRead, navigate]
    );

    return (
        <NotificationBell
            unreadCount={unreadCount}
            renderPanel={(isOpen, onClose) => (
                <NotificationPanel
                    isOpen={isOpen}
                    onClose={onClose}
                    title={t("notifications.title", "Notifications")}
                    unreadCount={unreadCount}
                    notifications={allNotifications}
                    isLoading={isLoading}
                    isLoadingMore={isLoadingMore}
                    filters={NOTIFICATION_FILTERS}
                    selectedFilter={selectedFilter}
                    onFilterChange={handleFilterChange}
                    onMarkAllAsRead={handleMarkAllAsRead}
                    onLoadMore={handleLoadMore}
                    pageInfo={{
                        current: currentPage,
                        total: notificationsData?.totalPages || 1,
                    }}
                    pageInfoText={t("notifications.pageInfo", "Page {{current}} of {{total}}", {
                        current: currentPage,
                        total: notificationsData?.totalPages || 1,
                    })}
                    renderNotification={(notification) => {
                        const config = NOTIFICATION_TYPE_MAP[notification.key];
                        const title = config
                            ? t(config.label, notification.key)
                            : notification.key;

                        return (
                            <NotificationItem
                                key={notification.id}
                                title={title}
                                content={notification.content}
                                date={formatRelativeTime(notification.createdAt)}
                                icon={config?.icon}
                                colorClass={config?.color}
                                isRead={notification.isRead}
                                isLoading={markingIds.includes(notification.id)}
                                onMarkAsRead={() => handleMarkAsRead(notification.id)}
                                onClick={() => handleNotificationClick(notification, onClose)}
                            />
                        );
                    }}
                />
            )}
        />
    );
};
